import { ReactComponent as SelectIcon } from "assets/morning/icon/select.svg";
import { useEffect, useRef, useState } from "react";

type SelectBoxProps = {
    className?: string;
    options?: { value: string; label: string }[];
    onChange?: (value: string) => void;
    value?: string;
    placeholder?: string;
    disabled?: boolean;
    error?: boolean;
    label?: string;
    height?: string;
    placement?: string;
    searchable?: boolean;
    searchPlaceholder?: string;
};
const SelectBox = ({
    className,
    options = [],
    onChange,
    placeholder,
    value,
    disabled,
    error = false,
    label,
    height,
    placement,
    searchable = false,
    searchPlaceholder,
}: SelectBoxProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [selectedOption, setSelectedOption] = useState<{ value: string; label: string } | undefined>(
        options.find(option => option.value === value),
    );

    const selectBoxRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const filteredOptions = searchable
        ? options.filter(option => option.label.toLowerCase().includes(searchText.toLowerCase()))
        : options;

    const handleSelect = (selectedValue: string) => {
        if (disabled) return;
        setSearchText("");
        setIsOpen(false);
        const foundOption = options.find(option => option.value === selectedValue);
        if (foundOption) {
            setSelectedOption(foundOption);
        }
        onChange?.(selectedValue);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (selectBoxRef.current && !selectBoxRef.current.contains(event.target as Node)) {
            setIsOpen(false);
            setSearchText("");
        }
    };

    useEffect(() => {
        const foundOption = options.find(option => option.value === value);
        if (foundOption?.value !== selectedOption?.value) {
            setSelectedOption(foundOption);
        }
    }, [value, options]);

    useEffect(() => {
        if (value === undefined || value === null || value === "") {
            setSelectedOption(undefined);
        }
    }, [value]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            if (searchable) {
                // 드롭다운이 열리면 검색 입력창에 포커스
                requestAnimationFrame(() => searchInputRef.current?.focus());
            }
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const handleToggle = () => {
        if (disabled) return;
        setIsOpen(prev => {
            const next = !prev;
            if (!next) setSearchText("");
            return next;
        });
    };

    return (
        <div className={`relative rounded-lg ${className} text-[18px]`} ref={selectBoxRef}>
            <div
                className={`${height ? height : "h-[55px]"} border  flex items-center justify-between rounded-lg px-3
                    ${disabled ? "cursor-default bg-gray-100 text-gray-400" : "cursor-pointer"}
                    ${error ? "border-red-500 border-2" : isOpen ? "border-new border-2" : "border border-gray-300"}`}
                onClick={handleToggle}
            >
                {searchable && isOpen ? (
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        placeholder={selectedOption?.label || searchPlaceholder || placeholder}
                        className="w-full h-full bg-transparent outline-none focus:outline-none focus:ring-0 border-0 text-gray-900 placeholder:text-gray-400"
                    />
                ) : (
                    <span className={`${!selectedOption ? "text-gray-400" : label ? label : "text-gray-900"}`}>
                        {selectedOption?.label || placeholder || "선택하세요"}
                    </span>
                )}
                <SelectIcon className={`transform transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </div>

            {isOpen && (
                <div
                    id="select-dropdown"
                    className={`absolute w-full p-2 text-sm mt-1 bg-white border border-gray4 rounded-lg shadow-lg z-20 ${placement}`}
                >
                    <div className={`overflow-y-auto ${searchable ? "max-h-[240px]" : ""}`}>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => (
                                <div
                                    key={option.value}
                                    className={`px-3 rounded-lg py-2 cursor-pointer transition-colors duration-150 ${
                                        selectedOption?.value === option.value ? "bg-new bg-opacity-10 pointer-events-none" : ""
                                    }`}
                                    onClick={() => handleSelect(option.value)}
                                >
                                    {option.label}
                                </div>
                            ))
                        ) : (
                            <p className="text-center text-grayicon py-2">결과가 없습니다.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SelectBox;
